/**
 * 状態値の日本語ラベル。
 *
 * enum 値（英語）と表示（日本語）を分離しておくことで、DB / API / ログは
 * 安定した英語の語彙のまま、画面だけを日本語にできる。
 * ここに無いキーは値をそのまま表示する（`labelOf` のフォールバック）。
 */

export const JA_LABELS: Record<string, string> = {
  /* 契約 */
  pending: '保留中',
  active: '有効',
  suspended: '停止中',
  closing: '解約手続き中',
  closed: '解約済み',
  trialing: '試用中',
  past_due: '支払い遅延',
  canceled: 'キャンセル済み',

  /* 請求 */
  draft: '下書き',
  open: '未払い',
  paid: '支払い済み',
  void: '無効',
  uncollectible: '回収不能',

  /* RBAC */
  superadmin: 'システム管理者',
  operator: '運用担当',
  support: 'サポート',
  auditor: '監査',
  org_owner: '契約オーナー',
  org_admin: '管理者',
  org_member: '一般ユーザー',
  staff: '運営者',
  org_user: '契約者ユーザー',
  agent: 'エージェント',
  system: 'システム',
  api_key: 'APIキー',

  /* ノード */
  provisioning: '構築中',
  enrolling: '登録中',
  draining: '退避中',
  maintenance: 'メンテナンス',
  decommissioned: '撤去済み',
  healthy: '正常',
  degraded: '劣化',
  /*
   * AI 接続先の `down`（`AI_ENDPOINT_HEALTH`）。ノードの `unreachable` と
   * 訳し分けてあるのは、こちらが「疎通が続けて失敗し、ルーティングの候補から
   * 外れている」という**運用上の状態**で、応答の有無そのものではないため。
   */
  down: '停止中（候補から除外）',
  critical: '重大',
  unreachable: '応答なし',
  unknown: '不明',
  applying: '適用中',
  applied: '適用済み',
  drifted: '設定差分あり',
  frozen: '署名検証に失敗し停止中',
  mx: '受信(MX)',
  'mx+submission': '受信+送信',
  'submission-only': '送信専用',
  'control-plane': 'コントロールプレーン管理',

  /*
   * AI のデータレジデンシー（`AI_DATA_RESIDENCY` / `AI_RESIDENCY_REQUIREMENT`）。
   * **法域の話**であって設置場所の話ではない（リージョン名は
   * `jp-east` のようにハイフンを含むので、この表の検査対象外）。
   * `any` を「指定なし」と訳しているのは、「どこでもよい」と読まれると
   * 「国外に出てもよい」という契約上の意思表示に見えるため。
   */
  jp: '日本国内',
  us: '米国',
  eu: '欧州（EU）',
  any: '指定なし',

  /* ドメイン / DNS */
  pending_verification: '所有権確認待ち',
  verifying: '確認中',
  dns_error: 'DNS異常',
  deleting: '削除中',
  ok: '正常',
  missing: '未設定',
  mismatch: '値が不一致',
  error: 'エラー',
  unchecked: '未確認',
  ownership_txt: '所有権確認 TXT',
  spf: 'SPF',
  dkim: 'DKIM',
  dmarc: 'DMARC',
  autoconfig: '自動設定 (Thunderbird)',
  autodiscover: '自動設定 (Outlook)',
  mta_sts: 'MTA-STS',
  tlsa: 'DANE (TLSA)',
  tls_rpt: 'TLS レポート (TLS-RPT)',
  none: 'なし',
  quarantine: '隔離',
  reject: '拒否',
  softfail: 'ソフトフェイル (~all)',
  fail: 'ハードフェイル (-all)',
  rsa2048: 'RSA 2048bit',
  rsa4096: 'RSA 4096bit',
  ed25519: 'Ed25519',

  /* メールボックス */
  over_quota: '容量超過',
  readonly: '読み取り専用',
  deleted: '削除済み',
  retiring: '退役準備中',
  tag: '件名にタグ付け',
  move_to_junk: '迷惑メールへ移動',
  argon2id: 'Argon2id',
  blf_crypt: 'bcrypt',

  /* カスタムホスト名（0017。CUSTOM_HOSTNAME_KIND） */
  /*
   * `webmail` のラベルは証明書の用途（`certificate_purpose`）と**同じキーを
   * 共有している**。このファイルは値をキーにした平坦なマップなので、
   * 同じ文字列の語彙は 1 つのラベルしか持てない。
   * 「Webメール」で意味が通る両者なので、証明書側の文言を
   * 用途が分かる形に保ったまま流用する。
   * **別の意味の語彙で同じ文字列を足すときは、必ずここを読み直すこと。**
   */
  link: '配信リンク',

  /* 証明書 */
  node_mail: 'ノード共用証明書',
  domain_mail: '独自ドメイン証明書',
  webmail: 'Webメール証明書',
  issuing: '発行中',
  renewing: '更新中',
  /** `CERT_FILTER` の絞り込み値。「まだ有効だが期限が近い」を表す。 */
  expiring: '期限が近い',
  expired: '期限切れ',
  revoked: '失効',

  /* レピュテーション監視の登録状態（`REPUTATION_PROGRAM_STATUS`・R48） */
  /** 一度も登録していない。**未登録であること自体を画面に出す**のが目的。 */
  not_registered: '未登録',
  registered: '登録済み',

  /* ジョブ */
  queued: '待機中',
  running: '実行中',
  waiting: '外部待ち',
  succeeded: '成功',
  failed: '失敗',
  compensating: 'ロールバック中',
  compensated: 'ロールバック済み',
  skipped: 'スキップ',
  organization_onboard: '契約開通',
  domain_onboard: 'ドメイン開通',
  domain_offboard: 'ドメイン廃止',
  mailbox_create: 'メールボックス作成',
  mailbox_update: 'メールボックス更新',
  mailbox_delete: 'メールボックス削除',
  node_enroll: 'ノード登録',
  node_drain: 'ノード退避',
  certificate_issue: '証明書発行',
  certificate_renew: '証明書更新',
  plan_change: 'プラン変更',
  webmail_sync: 'Webメール同期',
  stack_update: 'スタック更新',

  /* アップデート配信 */
  canary: 'カナリア',
  ring1: 'リング1',
  ring2: 'リング2',
  paused: '一時停止',
  halted: '中断',
  completed: '完了',
  rolled_back: 'ロールバック済み',
  updating: '更新中',

  /* セキュリティ */
  block: '遮断',
  allow: '許可',
  manual: '手動',
  auth_bruteforce: '認証総当たり',
  spam_abuse: 'スパム送信',
  rate_limit: 'レート超過',

  /* Webmail 連携 */
  synced: '同期済み',
  disabled: '無効',

  /* コンテンツ CMS */
  campaign: 'キャンペーン',
  template: 'テンプレート',
  snippet: 'スニペット',
  layout: 'レイアウト',
  'blocks-v1': 'ブロック編集',
  'raw-html-v1': 'HTML 直接編集',
  in_review: '承認待ち',
  approved: '承認済み',
  changes_requested: '差し戻し',
  pending_upload: 'アップロード待ち',
  committed: '確定済み',
  abandoned: '破棄',
  transient: '一時保存',
  sent_evidence: '送信済み証跡',
  portal: '管理画面',
  api: 'API 連携',
  import: '取り込み',
  document: '文書',
  asset: '画像・ファイル',
  both: '文書と画像',
  resize: 'リサイズ',
  format: '形式変換',
  thumbnail: 'サムネイル',
  archive: 'アーカイブする',
  unarchive: 'アーカイブ解除する',
  done: '実行済み',
  cancelled: '取り消し済み',
  redirect: 'リダイレクト',
  client_error: 'リンク切れ (4xx)',
  server_error: 'リンク先の障害 (5xx)',
  timeout: 'タイムアウト',
  blocked: '遮断（内部アドレス）',
  json_bundle: 'JSON バンドル',
  html_file: 'HTML ファイル',
  aurora_template: 'Webメールのテンプレート',
  uploaded: 'アップロード済み',
  validating: '検証中',
  preview_ready: 'プレビュー可',

  /* キャンペーン */
  validated: '事前チェック済み',
  test_sent: 'テスト送信済み',
  awaiting_release: '解禁待ち',
  scheduled: '配信予約済み',
  sending: '配信中',

  /* Webhook */
  disabled_by_failure: '連続失敗により停止',
  in_flight: '送信中',
  exhausted: '再試行上限に到達',

  /* 宛先の状態 */
  subscribed: '配信中',
  unsubscribed: '配信停止',
  pending_optin: '確認待ち',
  cleaned: '整理済み',
  bounced: '宛先不明',
  complained: '苦情あり',

  /* 配信停止の理由 */
  hard_bounce: '宛先不明',
  // 一時的な失敗（満杯・混雑）が連続したときの期限付き抑止。R0 §2.3 で追加。
  soft_bounce: '一時的な配送失敗',
  complaint: '苦情',
  unsubscribe: '本人の申し出',
  spamtrap: '不正な宛先',

  /* 同意の取得方法 */
  double_optin: '申込後に確認メールで同意',
  single_optin: '申込フォームのみ',
  offline: '書面・対面など',
  imported_with_evidence: '既存名簿からの移行（証跡あり）',
  manual_ops: '運営による解除',
  resubscribe: '本人による再登録',

  /* レビューの決裁・テンプレートの公開範囲・アセット */
  withdrawn: '取り下げ',
  org: '自組織',
  platform: '共通（運営提供）',
  uploading: 'アップロード中',
  quarantined: '隔離中',

  /* 出口 IP と送信ストリーム */
  transactional: 'トランザクション',
  bulk: '一斉配信',
  // 併用出口（0078）。**「共有」と訳さない** —— 0076 以来
  // 「共有 = 専有ではない」の意味で使っており、二重の意味になる。
  mixed: '併用出口',
  // `unknown`（用途を帰属できなかった分）は既出のラベルを使う。
  // `quarantine` は「隔離用プール」、`quarantined` は「隔離された状態」。
  // 日本語では同じ語になりやすいので、プール側は用途が分かる表記にする。
  warmup: 'ウォームアップ',
  standard: '標準',
  penalty: '制裁',
  retired: '廃止',
  warming: 'ウォームアップ中',
  clean: '掲載なし',
  listed: 'ブラックリスト掲載',
  delisting: '解除申請中',
  verified: '検証済み',
  throttled: '流量制限中',
  default: '既定',
  migration: '移行',

  /* 支払サイト（PAYMENT_TERMS）。法人取引では「月末締め翌月末払い」のような
     締め日基準が一般的で、全社一律 30 日固定では稟議に載らない。
     `net30` のような英字を画面へ出しても意味が通らないので必ず訳す。 */
  prepaid: '前払い（発行時）',
  net15: '発行日から 15 日',
  net30: '発行日から 30 日',
  net60: '発行日から 60 日',
  eom_next_15: '月末締め翌月 15 日払い',
  eom_next_25: '月末締め翌月 25 日払い',
  eom_next_eom: '月末締め翌月末払い',

  /* 税率区分（適格請求書は税率ごとに区分して記載する） */
  // `standard`（標準）は上の送信ストリームの語彙と同じ値なので再掲しない。
  reduced: '軽減税率',
  exempt: '非課税',

  /* 配信実行 */
  leased: '配信待機中',
  injected: '投入済み',
  delivered: '到達',
  soft_bounced: '一時エラー',
  hard_bounced: '宛先不明',
  suppressed: '配信除外',
  deferred: '再送待ち',
  replied: '返信あり',
  clicked: 'クリック',
  oneclick_http: 'ワンクリック解除（HTTP）',
  oneclick_mailto: 'ワンクリック解除（メール）',
  preference_center: '設定ページから',

  /* 公開フォーム */
  published: '公開中',
  archived: 'アーカイブ済み',
  pending_confirm: '確認メール送信済み',
  confirmed: '確認済み',
  rejected: '拒否',
  off: '使わない',
  auto: '自動判定',
  always: '常に要求',
  content_revision: '本文の版',
  content_artifact: 'ビルド成果物',

  /* 申込フォームの入力欄の種別（SIGNUP_FIELD_KIND） */
  // 種別は閉じた列挙。**自由な HTML を置ける種別を足さないこと**（CD §7）。
  email: 'メールアドレス',
  text: 'テキスト',
  select: '選択',
  checkbox: 'チェックボックス',

  /* エディタの語彙（ブロック・レイアウト・装飾） */
  heading: '見出し',
  button: 'ボタン',
  image: '画像',
  divider: '区切り線',
  spacer: '余白',
  html: 'HTML',
  legal_footer: '法定表示',
  single: '1 段組み',
  two_column: '2 段組み',
  three_column: '3 段組み',
  bold: '太字',
  italic: '斜体',
  underline: '下線',
  strike: '打ち消し線',

  /* 差し込みキー（システム提供） */
  unsubscribe_url: '配信停止 URL',
  preferences_url: '設定ページ URL',
  web_version_url: 'Web 版 URL',
  today: '本日の日付',
  address: '住所',

  /* 検証結果の重大度・スケジュールの操作・サプレッションの範囲 */
  // `blocking` は「送信を通さない」。`warning` と混ぜて表示しないこと
  // （どちらも黄色くすると、止まる不備と止まらない不備の区別が消える）。
  blocking: '要修正（送信不可）',
  warning: '警告',
  info: '参考',
  approve: '承認',
  organization: '組織全体',
  stored: '保存済み',

  /* エージェントからの通報（AGENT_EVENT_KIND） */
  auth_failure_burst: '認証失敗の多発',
  ip_banned: 'IP の遮断',
  ip_unbanned: 'IP の遮断解除',
  relay_attempt: '不正中継の試行',
  sender_mismatch: '差出人の不一致',
  spam_outbound: '迷惑メールの送出',
  send_rate_exceeded: '送信レート超過',
  queue_backlog: 'キューの滞留',
  disk_pressure: 'ディスク逼迫',
  tls_handshake_failure: 'TLS ハンドシェイク失敗',
  signature_invalid: '設定の署名検証に失敗',

  /* 法定ページ（LEGAL_DOCUMENT_SLUGS / SIGNUP_CONSENT_KINDS） */
  // `LEGAL_DOCUMENT_TITLES_JA` と同じ文言にする。片方だけ直すと
  // 画面のパンくずとページ見出しが食い違う。
  terms: '利用規約',
  privacy: 'プライバシーポリシー',
  aup: '禁止事項（Acceptable Use Policy）',
  sla: 'サービス品質目標（SLA）',
  tokushoho: '特定商取引法に基づく表記',
  subcontractors: '委託先一覧',
  data_access: '契約者データへのアクセス手続',

  /* 苦情フィードバック（ARF_FEEDBACK_TYPES / RFC 5965）。
     `not-spam` と `auth-failure` はハイフンを含むのでこの表の対象外
     （語彙検査が `[a-z0-9_]+` の値だけを見る）。画面に出すときは
     `ARF_FEEDBACK_TYPE_LABELS_JA` を使うこと。 */
  abuse: '迷惑メール報告',
  fraud: '詐欺・フィッシングの報告',
  virus: 'ウイルスの報告',
  other: 'その他',

  /* エージェント面のサービス資格情報（AGENT_SERVICE_CREDENTIAL_KIND）。
     兄弟の `NODE_CREDENTIAL_KIND` は「画面に出ない」として
     `labels.ja.test.ts` の INTERNAL_VOCABULARIES に入っているが、
     こちらは入っていないのでラベルが要る。Webメールの接続が切れたときに
     運営が最初に見るのがこの資格情報なので、日本語で出て困るものでもない。 */
  dovecot_master: 'Dovecot マスターユーザー',

  /* 認証イベントの経路と結果（AGENT_AUTH_SERVICE / AGENT_AUTH_RESULT・R134）。
     契約者面の「ログイン履歴」にそのまま出る。IMAP / POP3 は略語のまま
     残すのが正しい（契約者のメールソフトの設定画面にもその名前で出る）。 */
  imap: 'IMAP（メール受信）',
  pop3: 'POP3（メール受信）',
  smtp: 'SMTP（メール受信サーバー）',
  submission: 'メール送信（Submission）',
  managesieve: '振り分けルールの編集（ManageSieve）',
  success: '成功',
  failure: '失敗',

  /* 受信者のプライバシー保護（REMOTE_IMAGE_MODE / PROXY_EGRESS_MODE / TRACKER_VERDICT）。
     `block` / `blocked` / `clean` / `none` は既に上で別の意味で使われている値なので
     ここには足せない（この表は値をキーにした平坦なマップ）。判定結果を画面へ出すときは
     意味の合った `TRACKER_VERDICT_LABELS_JA` を使うこと。 */
  proxy: 'こちらで代理取得（推奨）',
  direct: '受信者が直接取得',
  fixed: '固定',
  rotate: '毎回入れ替える',
  tracking_pixel: '開封追跡ピクセル',
  known_tracker: '既知の計測事業者',

  /* Webメールの AI（AI_PROVIDER / AI_FEATURE / AI_LIMIT_ACTION / AI_SYNC_STATE /
     AI_PRIVACY_LEVEL）。プロバイダ名は固有名詞なので訳さない。
     `replySuggest` / `nlSearch` はキャメルケースで語彙検査の対象外なので、
     画面では `AI_FEATURE_LABELS_JA` を使うこと。 */
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  openai_compat: 'OpenAI 互換 API（接続先を指定）',
  ollama: 'Ollama（自社設置）',
  summarize: 'スレッドの要約',
  classify: '自動分類',
  workflow: 'ワークフロー実行',
  translate: '本文の翻訳',
  meter: '従量課金で継続する',
  not_applicable: '対象外',
  metadata_only: '件名と差出人のみ',
  excerpt: '本文の冒頭のみ',
  full_body: '本文全体',

  /* AI アグリゲータのプリセット（AI_PRESET / AI_PRESET_RESIDENCY_UNKNOWN・0060）。
     サービス名は固有名詞なので訳さない。**括弧の中に「処理先が選べない」ことを
     書いておく**（一覧だけを見て選ぶ契約者に、いちばん重要な性質を伝える）。 */
  openrouter: 'OpenRouter（処理先の国は選べません）',
  together: 'Together AI（処理先の国は選べません）',
  groq: 'Groq（処理先の国は選べません）',
  fireworks: 'Fireworks AI（処理先の国は選べません）',
  deepinfra: 'DeepInfra（処理先の国は選べません）',
  azure_openai: 'Azure OpenAI（自社リソース）',
  litellm: 'LiteLLM プロキシ（自社設置）',
  vllm: 'vLLM（自社設置）',
  /* Amazon Bedrock（0070）。**「処理する国が確定する」ことを括弧に書く**。
     アグリゲータとの違いはそこにしかなく、そこが上位プランの売りだから。 */
  bedrock: 'Amazon Bedrock（処理する国が確定します）',

  /* プライベートメールアドレス（PRIVATE_ADDRESS_STATUS / PRIVATE_ADDRESS_CREATED_VIA）。
     `active` / `disabled` / `portal` / `api` は上で定義済みなのでここには足さない。
     `burned` だけが新しい語彙。**「止めた」と「漏らされたので捨てた」を
     同じ言葉にしない**（漏洩元の統計が取れなくなる）。 */
  burned: '漏洩により破棄',
};

/**
 * 画像の判定結果。**`JA_LABELS` の `clean` / `blocked` とは意味が違う**ので別表にする
 * （あちらは RBL の掲載状況と内部アドレスの遮断）。
 */
export const TRACKER_VERDICT_LABELS_JA: Readonly<Record<string, string>> = {
  clean: '通常の画像',
  tracking_pixel: '開封追跡ピクセル（表示しません）',
  known_tracker: '既知の計測事業者（表示しません）',
  blocked: '取得を断りました',
};

/**
 * AI の機能スイッチ。値がキャメルケース（zod のキーと一致させてある）ため
 * `JA_LABELS` の語彙検査の対象外になるので、ここで別に持つ。
 */
export const AI_FEATURE_LABELS_JA: Readonly<Record<string, string>> = {
  summarize: 'スレッドの要約',
  replySuggest: '返信文の提案',
  nlSearch: '自然文での検索',
  classify: '自動分類とルール提案',
  workflow: 'ワークフロー実行',
  translate: '本文の翻訳',
};

/**
 * `Feedback-Type` の日本語ラベル。**ハイフンを含む値があるので別表**にしてある
 * （`JA_LABELS` の語彙検査は `[a-z0-9_]+` の値しか拾わないため、
 * ここへ入れないと `not-spam` だけ英語のまま画面に出る）。
 */
export const ARF_FEEDBACK_TYPE_LABELS_JA: Readonly<Record<string, string>> = {
  abuse: '迷惑メール報告',
  fraud: '詐欺・フィッシングの報告',
  virus: 'ウイルスの報告',
  'not-spam': '報告の取り消し（迷惑メールではない）',
  'auth-failure': '送信者認証の失敗報告',
  other: 'その他',
};

export function labelOf(value: string | null | undefined): string {
  if (!value) return '—';
  return JA_LABELS[value] ?? value;
}
